import React, { useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { buildAssigneeTransferOptions } from "../utils/assigneeTransferField";
import {
  assigneeFilterSelectionLabels,
  condicoesToAssigneeSelection,
  normalizeAssigneeFilterSelection,
} from "../utils/assigneeFilterField";

/**
 * Select unificado para condições de automação (filtro por responsável).
 * Permite múltipla seleção de usuários, ChatBots e agentes de IA.
 */
export default function AssigneeFilterField({
  label = "Responsável",
  values = {},
  users = [],
  agents = [],
  excludeAgentIds = [],
  loading = false,
  disabled = false,
  helperText,
  errorText,
  onApply,
}) {
  const theme = useTheme();

  const options = useMemo(
    () => buildAssigneeTransferOptions({ users, agents, excludeAgentIds }),
    [users, agents, excludeAgentIds]
  );

  const selected = useMemo(
    () => condicoesToAssigneeSelection(values, { users, agents }),
    [values, users, agents]
  );

  const handleChange = (_, nextRaw) => {
    const nextArr = Array.isArray(nextRaw) ? nextRaw : nextRaw ? [nextRaw] : [];
    const normalized = normalizeAssigneeFilterSelection(nextArr);
    const labels = assigneeFilterSelectionLabels(normalized);
    onApply?.(normalized, labels);
  };

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={options}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option?.label || ""}
      isOptionEqualToValue={(a, b) =>
        a?.kind === b?.kind && String(a?.id ?? "") === String(b?.id ?? "")
      }
      value={selected}
      onChange={handleChange}
      loading={loading}
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          margin="dense"
          label={label}
          error={!!errorText}
          helperText={
            errorText ||
            helperText ||
            "Filtro por responsável: usuários, ChatBots ou agentes de IA (qualquer um selecionado)."
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={`${option.kind}-${option.id}`}
          style={{
            ...props.style,
            fontWeight: option.kind === "agent" ? 600 : option.kind === "chatbot" ? 500 : 400,
            color:
              option.kind === "agent"
                ? theme.palette.secondary.main
                : option.kind === "chatbot"
                ? theme.palette.warning?.main || "#ed6c02"
                : undefined,
          }}
        >
          {option.label}
        </li>
      )}
    />
  );
}
